import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, Users, Target } from 'lucide-react';
import { KpiCard } from '@/components/kpi-card';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { fetchUtilisationData } from '@/lib/data-service';
import { formatPercent, getUtilisationColor } from '@/lib/utils';
import type { UtilisationData } from '@/types';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';

export function UtilisationPage() {
  const [loading, setLoading] = useState(true);
  const [utilisation, setUtilisation] = useState<UtilisationData[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await fetchUtilisationData('utilisation');
        setUtilisation(data);
      } catch (error) {
        handleApiError(error, '/api/utilisation', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError, currentCompany?.id]);

  const avgUtilisation =
    utilisation.reduce((sum, u) => sum + u.utilisationPercent, 0) / utilisation.length || 0;
  const underUtilised = utilisation.filter(u => u.status === 'under').length;
  const overAllocated = utilisation.filter(u => u.status === 'over').length;
  const onTarget = utilisation.filter(u => u.status === 'target').length;

  const getStatusBadge = (status: 'under' | 'target' | 'over') => {
    switch (status) {
      case 'under':
        return <Badge variant="warning">Under-Utilised</Badge>;
      case 'target':
        return <Badge variant="success">On Target</Badge>;
      case 'over':
        return <Badge variant="destructive">Over-Allocated</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Team Utilisation"
        subtitle="Track billable hours and resource capacity"
        size={20}
        showActions={false}
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4" data-tour="utilisation-summary-cards">
        <KpiCard
          title="Avg Utilisation"
          value={formatPercent(avgUtilisation, 1)}
          icon={Users}
          description="Target: 75%"
          accentVariant={avgUtilisation >= 75 ? 'success' : avgUtilisation >= 50 ? 'warning' : 'error'}
        >
          <Progress value={avgUtilisation} className="mt-2 h-2" />
        </KpiCard>

        <KpiCard
          title="On Target"
          value={onTarget}
          icon={Target}
          description="70-100% utilised"
          accentVariant="success"
        />

        <KpiCard
          title="Under-Utilised"
          value={underUtilised}
          icon={TrendingDown}
          description="Below 70%"
          accentVariant={underUtilised > 0 ? 'warning' : 'success'}
        />

        <KpiCard
          title="Over-Allocated"
          value={overAllocated}
          icon={TrendingUp}
          description="Above 100%"
          accentVariant={overAllocated > 0 ? 'error' : 'success'}
        />
      </div>

      {/* Utilisation Table */}
      <Card data-tour="utilisation-table">
        <CardHeader className="mt-0 pt-6 pb-4">
          <CardTitle>Team Utilisation Details</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader divided>
              <TableRow className="hover:bg-transparent">
                <TableHead>Employee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Billable Hours</TableHead>
                <TableHead className="text-right">Available Hours</TableHead>
                <TableHead className="text-right">Utilisation</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Loading utilisation data...
                  </TableCell>
                </TableRow>
              ) : (
                utilisation.map(u => (
                  <TableRow key={u.employeeId} className="hover:bg-transparent">
                    <TableCell>
                      <div className="font-medium">{u.employeeName}</div>
                    </TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>{u.department}</TableCell>
                    <TableCell className="text-right">{u.billableHours.toFixed(0)}h</TableCell>
                    <TableCell className="text-right">{u.availableHours}h</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-semibold ${getUtilisationColor(u.utilisationPercent)}`}>
                        {formatPercent(u.utilisationPercent, 1)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatPercent(u.targetPercent, 0)}
                    </TableCell>
                    <TableCell>{getStatusBadge(u.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={Math.min(u.utilisationPercent, 100)}
                          className={`h-2 w-24 ${
                            u.status === 'over'
                              ? '[&>div]:bg-error-600'
                              : u.status === 'under'
                              ? '[&>div]:bg-warning-600'
                              : '[&>div]:bg-success-600'
                          }`}
                        />
                        {u.utilisationPercent > 100 && (
                          <span className="text-xs text-error-600 font-semibold">
                            +{formatPercent(u.utilisationPercent - 100, 0)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Insights */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">💡 Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-success-600 mt-2" />
              <div>
                <p className="text-sm font-medium">Strong Overall Performance</p>
                <p className="text-xs text-muted-foreground">
                  Team average of {formatPercent(avgUtilisation, 1)} is above the 75% target
                </p>
              </div>
            </div>
            {overAllocated > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-error-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Over-Allocation Risk</p>
                  <p className="text-xs text-muted-foreground">
                    {overAllocated} team member{overAllocated > 1 ? 's are' : ' is'} over 100% allocated
                  </p>
                </div>
              </div>
            )}
            {underUtilised > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Capacity Available</p>
                  <p className="text-xs text-muted-foreground">
                    {underUtilised} team member{underUtilised > 1 ? 's have' : ' has'} available capacity
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">📊 Utilisation Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">On Target (70-100%)</span>
                  <span className="text-sm font-semibold text-success-600">
                    {onTarget} ({formatPercent((onTarget / utilisation.length) * 100, 0)})
                  </span>
                </div>
                <Progress
                  value={(onTarget / utilisation.length) * 100}
                  className="h-2 [&>div]:bg-success-600"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Under-Utilised (&lt;70%)</span>
                  <span className="text-sm font-semibold text-warning-600">
                    {underUtilised} ({formatPercent((underUtilised / utilisation.length) * 100, 0)})
                  </span>
                </div>
                <Progress
                  value={(underUtilised / utilisation.length) * 100}
                  className="h-2 [&>div]:bg-warning-600"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Over-Allocated (&gt;100%)</span>
                  <span className="text-sm font-semibold text-error-600">
                    {overAllocated} ({formatPercent((overAllocated / utilisation.length) * 100, 0)})
                  </span>
                </div>
                <Progress
                  value={(overAllocated / utilisation.length) * 100}
                  className="h-2 [&>div]:bg-error-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
